/**
 * custom-xml-part — Office.js adapter for the doc-level metadata
 * registry (`urn:theplaidoyer:docx:1` v="1"). Only home (with
 * content-control.ts) for `Word.run` in the entire add-in.
 *
 * Implements ChipStoragePort over `DocumentMetadata` from
 * `@/lib/xml-schema` (the on-disk shape).
 */
import {
  serialiseXmlPart,
  parseXmlPart,
  XML_NAMESPACE,
  type DocumentMetadata,
  type XmlCitation,
  XmlParseError,
} from '@/lib/xml-schema'
import { OfficeAdapterError } from './errors'

/* eslint-disable @typescript-eslint/no-explicit-any */
type WordCtx = any
type WordXmlPart = any
/* eslint-enable @typescript-eslint/no-explicit-any */

/** Minimal shape backfill / reconcile need from a chip. */
export interface ChipTagSnapshot {
  cid: string
  refKey: string
  insertedAt: string
  score: number
}

/** Plan-§3.4: insertedAt-driven backfill heuristic. */
const SEVEN_DAYS_MS = 7 * 24 * 60 * 60 * 1000
const ONE_HOUR_MS = 60 * 60 * 1000

function backfillStatus(insertedAt: string): 'stale' | 'unverified' {
  // Per plan §3.4 (and §2.6 last row): silent backfill from CCs with
  // insertedAt heuristic. For now this adapter is conservative and
  // ALWAYS returns 'stale' to avoid lying to the lawyer about the
  // freshness of the score. The 1-hour shortcut is delivered in PR-3c
  // alongside `Vérifier tout`.
  void insertedAt
  void SEVEN_DAYS_MS
  void ONE_HOUR_MS
  return 'stale'
}

function getWord(): {
  run: <T>(batch: (ctx: WordCtx) => Promise<T>) => Promise<T>
} {
  const w = (globalThis as Record<string, unknown>).Word as
    | { run: <T>(batch: (ctx: WordCtx) => Promise<T>) => Promise<T> }
    | undefined
  if (!w) {
    throw new OfficeAdapterError(
      'sync-failed',
      'Word global not installed (running outside Word host / not mocked)',
    )
  }
  return w
}

function mapSyncError(err: unknown): OfficeAdapterError {
  const e = err as { code?: string; message?: string }
  if (e?.code === 'InvalidOperation') {
    return new OfficeAdapterError('document-closed', e.message ?? '', err)
  }
  if (e?.code === 'GeneralException') {
    return new OfficeAdapterError('sync-failed', e.message ?? '', err)
  }
  return new OfficeAdapterError(
    'sync-failed',
    (err as Error)?.message ?? 'unknown',
    err,
  )
}

// ─── Public adapter shape ─────────────────────────────────────────

export interface CustomXmlPartAdapter {
  /** Ensure the theplaidoyer XmlPart exists with this metadata. If
   *  the part already exists, leaves it alone (no overwrite). */
  ensureMounted(meta: DocumentMetadata): Promise<void>

  /** Read the current metadata. Throws OfficeAdapterError
   *  `xml-part-missing` if no part exists. */
  read(): Promise<DocumentMetadata>

  /** Insert-or-update a single citation entry (by cid). */
  upsertCitation(cite: XmlCitation): Promise<void>

  /** Backfill the XmlPart from CC tags when no part exists yet (v0
   *  doc / post-copy-paste / etc.). Creates the part. */
  backfillFromChipTags(
    anchor: {
      briefId: string
      caseId: string
      snapshotVersion: number
      lang: 'fr' | 'ar' | 'en'
      styleProfile: string
    },
    chips: ReadonlyArray<ChipTagSnapshot>,
  ): Promise<void>

  /** Reconcile drift: remove XmlPart entries with no matching CC,
   *  re-create XmlPart entries from CC tags that have no entry. */
  reconcileDrift(
    chips: ReadonlyArray<ChipTagSnapshot>,
  ): Promise<{ removedOrphans: number; recreatedFromCC: number }>
}

// ─── Internal helpers ─────────────────────────────────────────────

async function findExistingPart(ctx: WordCtx): Promise<WordXmlPart | null> {
  const parts = ctx.document.customXmlParts.getByNamespace(XML_NAMESPACE)
  parts.load('items/id')
  await ctx.sync()
  const items = parts.items as WordXmlPart[]
  if (items.length === 0) return null
  return items[0]
}

async function readMetaFromPart(
  ctx: WordCtx,
  part: WordXmlPart,
): Promise<DocumentMetadata> {
  const xmlResult = part.getXml()
  await ctx.sync()
  try {
    return parseXmlPart(xmlResult.value)
  } catch (err) {
    if (err instanceof XmlParseError) {
      throw new OfficeAdapterError(
        'sync-failed',
        `XmlPart parse failed: ${err.kind}: ${err.message}`,
        err,
      )
    }
    throw err
  }
}

// ─── Adapter factory ──────────────────────────────────────────────

export function createCustomXmlPartAdapter(): CustomXmlPartAdapter {
  return {
    async ensureMounted(meta) {
      const Word = getWord()
      try {
        await Word.run(async (ctx: WordCtx) => {
          const existing = await findExistingPart(ctx)
          if (existing) return
          const xml = serialiseXmlPart(meta)
          ctx.document.customXmlParts.add(xml)
          await ctx.sync()
        })
      } catch (err) {
        if (err instanceof OfficeAdapterError) throw err
        throw mapSyncError(err)
      }
    },

    async read() {
      const Word = getWord()
      try {
        return await Word.run(async (ctx: WordCtx) => {
          const existing = await findExistingPart(ctx)
          if (!existing) {
            throw new OfficeAdapterError(
              'xml-part-missing',
              'no theplaidoyer XmlPart found',
            )
          }
          return await readMetaFromPart(ctx, existing)
        })
      } catch (err) {
        if (err instanceof OfficeAdapterError) throw err
        throw mapSyncError(err)
      }
    },

    async upsertCitation(cite) {
      const Word = getWord()
      try {
        await Word.run(async (ctx: WordCtx) => {
          const existing = await findExistingPart(ctx)
          if (!existing) {
            throw new OfficeAdapterError(
              'xml-part-missing',
              'cannot upsertCitation before XmlPart is mounted',
            )
          }
          const meta = await readMetaFromPart(ctx, existing)
          const idx = meta.citations.findIndex((c) => c.cid === cite.cid)
          if (idx >= 0) {
            meta.citations[idx] = cite
          } else {
            meta.citations.push(cite)
          }
          existing.setXml(serialiseXmlPart(meta))
          await ctx.sync()
        })
      } catch (err) {
        if (err instanceof OfficeAdapterError) throw err
        throw mapSyncError(err)
      }
    },

    async backfillFromChipTags(anchor, chips) {
      const Word = getWord()
      try {
        await Word.run(async (ctx: WordCtx) => {
          const meta: DocumentMetadata = {
            briefId: anchor.briefId,
            caseId: anchor.caseId,
            snapshotVersion: anchor.snapshotVersion,
            lang: anchor.lang,
            styleProfile: anchor.styleProfile,
            lastSyncAt: '1970-01-01T00:00:00Z',
            diffHash: '',
            citations: chips.map<XmlCitation>((c) => ({
              cid: c.cid,
              refKey: c.refKey,
              status: backfillStatus(c.insertedAt),
              score: c.score,
              insertedAt: c.insertedAt,
              label: '',
              codeName: '',
              articleNumber: '',
            })),
            bordereau: [],
          }
          const existing = await findExistingPart(ctx)
          const xml = serialiseXmlPart(meta)
          if (existing) {
            existing.setXml(xml)
          } else {
            ctx.document.customXmlParts.add(xml)
          }
          await ctx.sync()
        })
      } catch (err) {
        if (err instanceof OfficeAdapterError) throw err
        throw mapSyncError(err)
      }
    },

    async reconcileDrift(chips) {
      const Word = getWord()
      try {
        return await Word.run(async (ctx: WordCtx) => {
          const existing = await findExistingPart(ctx)
          if (!existing) {
            throw new OfficeAdapterError(
              'xml-part-missing',
              'cannot reconcileDrift before XmlPart is mounted',
            )
          }
          const meta = await readMetaFromPart(ctx, existing)
          const chipCids = new Set(chips.map((c) => c.cid))
          const existingCids = new Set(meta.citations.map((c) => c.cid))

          let removedOrphans = 0
          const keptCitations = meta.citations.filter((c) => {
            if (chipCids.has(c.cid)) return true
            removedOrphans += 1
            return false
          })

          let recreatedFromCC = 0
          for (const chip of chips) {
            if (existingCids.has(chip.cid)) continue
            keptCitations.push({
              cid: chip.cid,
              refKey: chip.refKey,
              status: 'stale', // conservative re-resolve marker
              score: chip.score,
              insertedAt: chip.insertedAt,
              label: '',
              codeName: '',
              articleNumber: '',
            })
            recreatedFromCC += 1
          }

          meta.citations = keptCitations
          existing.setXml(serialiseXmlPart(meta))
          await ctx.sync()
          return { removedOrphans, recreatedFromCC }
        })
      } catch (err) {
        if (err instanceof OfficeAdapterError) throw err
        throw mapSyncError(err)
      }
    },
  }
}
